// ProfileRequiredGate.js
//
// Generaliza `PhoneRequiredGate.js` (#1401) a los tres datos que se piden al
// iniciar sesión (#1416, R39-R46, R58): celular, etapa e institución. Pide
// SOLO los campos de `missing_profile_fields` (backend, `/api/user/me/`,
// R4) — el que ya tiene no aparece.
//
// Desde #1599 R8 el backend solo manda `["phone"]` o `[]`: etapa e institución
// se piden en Quién eres. Las ramas de etapa/institución de abajo quedan por si
// un backend viejo todavía las manda; con el backend actual no se dibujan.
//
// Reglas (v2 del spec, respuesta de Sebastián a las 3 preguntas abiertas):
//  - El celular NUNCA se salta: mientras falte, no hay "Después" ni cierre
//    sin guardar, solo Cerrar sesión (R43).
//  - Etapa e institución SÍ se pueden dejar para después con el botón
//    "Después" (R45) o guardando solo el celular y dejándolas vacías (R58,
//    mismo efecto). La marca vive en localStorage, atada a `login_session_id`
//    (R46, revisor fase 2 F2): el ID del último `LoginEvent` exitoso que
//    manda `/api/user/me/`, que SÍ cambia en cada login real — a diferencia
//    del token, que `ObtainAuthToken`/Google reusan con `get_or_create` (dos
//    logins de la misma persona daban el MISMO token, así que la marca
//    atada al hash del token nunca volvía a preguntar). Si `login_session_id`
//    viene `null`/`undefined` (backend viejo, o sin login registrado), cae al
//    hash del token — el comportamiento de antes — como red de seguridad.
//  - Un solo overlay a la vez (R44): reemplaza a `PhoneRequiredGate`, que se
//    borra. El criterio de "no mostrar sobre Términos/Suscripción" es el
//    MISMO que tenía el gate viejo (R40).
//
// ORDEN: igual que `PhoneRequiredGate` — después de `TosAcceptanceGate`,
// `SuscripcionGate` (el gate de documentos se borró en #1529).
import { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useCurrentUser } from "../context/UserContext";
import getClient from "../../helpers/get-client";
import AvisoWhatsapp from "../comunicaciones/AvisoWhatsapp";
import {
  PAISES,
  banderaPais,
  mensajeCelularInvalido,
  normalizeMobile,
  placeholderCelular,
  prefijoCelular,
  separarCelularGuardado,
} from "../../helpers/phone";
import { logout } from "../../helpers/authentication-helpers";
import { ETAPAS_SIN_HIJA } from "../../helpers/etapas";
// Sistema único de botones (#1416, R35, revisor fase 2 F8): mismo
// `AdminButton` que usa el panel de admin — no se duplica el estilo pill acá,
// se reusa el único que ya existe (el pedido del líder fue "sin duplicar
// estilos").
import { AdminButton } from "../../views/admin/users/ui";

const client = getClient();

// Códigos de cuenta bloqueada: ahí manda la pantalla de pago, no este gate.
// (#1529: `documents_required` ya no existe, el backend dejó de emitirlo.)
// `inactividad` (pausa por inactividad, #1583, R26): ahí manda la pantalla de
// pausa (PausaInactividadGate); con la cuenta pausada no se dibuja el gate de perfil.
const CODES_QUE_BLOQUEAN = new Set(["trial_expired", "subscription_past_due", "inactividad"]);

// #1738 R12: etiquetas de `helpers/etapas.js`. Este gate solo guarda la etapa, y las que piden una
// pregunta hija (becado, especialista, no soy médico) las rechaza el backend sin ella (R5): se
// ofrecen solo las que se guardan solas. Hoy `missing_profile_fields` ya no trae `etapa` (#1599 R8).
const ETAPA_OPCIONES = ETAPAS_SIN_HIJA;

const AUTH_TOKEN_KEY = "authToken";
const DESPUES_PREFIX = "smartdx:perfil-despues:";

// Hash corto, NO reversible, del token vigente — no hace falta más que
// distinguir "mismo login" de "login nuevo" (R46). Alcanza con un hash
// simple: si algún día colisiona, el peor caso es preguntar una vez de más.
const hashCorto = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(h, 31) + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

const despuesKey = (userId) => `${DESPUES_PREFIX}${userId}`;

// Marca de "Después" (#1416, R46, revisor fase 2 F2): se prefija `s:` cuando
// se ata a `login_session_id` (el caso normal, backend con la ronda 4) y
// `t:` cuando cae al hash del token (fallback, backend viejo o sin sesión de
// login todavía). Los dos prefijos evitan que una marca vieja tipo token se
// confunda con una nueva tipo sesión si el backend recién empieza a mandar
// `login_session_id` en un navegador que ya tenía algo guardado.
const marcarDespues = (userId, loginSessionId) => {
  try {
    const valor =
      loginSessionId !== null && loginSessionId !== undefined
        ? `s:${loginSessionId}`
        : `t:${hashCorto(localStorage.getItem(AUTH_TOKEN_KEY) || "")}`;
    localStorage.setItem(despuesKey(userId), valor);
  } catch {
    // localStorage bloqueado: no hay dónde guardar la marca, se volverá a
    // preguntar la próxima vez — conservador, no rompe nada.
  }
};

const yaDijoDespues = (userId, loginSessionId) => {
  try {
    const marca = localStorage.getItem(despuesKey(userId));
    if (!marca) return false;
    if (loginSessionId !== null && loginSessionId !== undefined) {
      return marca === `s:${loginSessionId}`;
    }
    return marca === `t:${hashCorto(localStorage.getItem(AUTH_TOKEN_KEY) || "")}`;
  } catch {
    return false;
  }
};

// R39-R44: qué falta y si corresponde mostrar el overlay ahora.
export const needsProfile = (user) => {
  if (!user) return false;
  const missing = user.missing_profile_fields || [];
  if (missing.length === 0) return false;
  if (user.tos_needs_acceptance) return false;
  const access = user.access;
  if (access && access.permite === false && CODES_QUE_BLOQUEAN.has(access.code)) {
    return false;
  }
  // R45/R46/R58: si lo único que falta es etapa/institución (el celular ya
  // es canónico) y esta sesión de login ya dijo "Después", no se vuelve a
  // mostrar hasta el próximo login. `login_session_id` es lo que distingue
  // login nuevo de login viejo (F2): el token solo no alcanza.
  if (!missing.includes("phone") && yaDijoDespues(user.id, user.login_session_id)) {
    return false;
  }
  return true;
};

// Portado de `PhoneRequiredGate.js` (#1401 ronda 5 + a699f2c) al integrar
// #1416, que reemplazó ese gate por este: con el prefijo "+56 9" fijo, un
// número viejo precargado tiene que mostrarse como SOLO los 8 dígitos que van
// después del 9. Solo precarga si lo guardado YA es un celular (9 + 8
// dígitos, con o sin 56 o 0 adelante): recortar "los últimos 8" de cualquier
// cosa convertía un fijo viejo (`+56 2 2345 6789`) en un celular inventado.
// Lo que no es celular deja el campo vacío.
// #1590: fuera de Chile la precarga la hace `separarCelularGuardado` (helpers/phone.js).
export const soloLosOchoDigitos = (raw) => {
  let digits = String(raw || "").replace(/\D/g, "");
  if (digits.startsWith("56") && digits.length === 11) digits = digits.slice(2);
  else if (digits.startsWith("0") && digits.length === 10) digits = digits.slice(1);
  if (digits.length !== 9 || !digits.startsWith("9")) return "";
  return digits.slice(1);
};

const ProfileRequiredGate = () => {
  const { currentUser, loading, refreshUser } = useCurrentUser();
  const missing = currentUser?.missing_profile_fields || [];
  const faltaPhone = missing.includes("phone");
  const faltaEtapa = missing.includes("etapa");
  const faltaDonde = missing.includes("donde_trabaja");

  const legacyPhone = currentUser?.professional_profile?.phone || "";
  // #1590: país del médico (Chile por defecto) → prefijo y regla del celular.
  // Un extranjero que entró con Google llega acá sin celular, y sin el país
  // quedaba trancado: el celular no tiene "Después".
  // Cubre R5 — .claude/specs/1590-paises-telefono-usd/
  const legacyCountry = currentUser?.professional_profile?.country || "";
  const [country, setCountry] = useState(() => legacyCountry || "CL");
  const precarga = (raw, iso) =>
    (iso || "CL") === "CL" ? soloLosOchoDigitos(raw) : separarCelularGuardado(raw, iso).local;
  const [phone, setPhone] = useState(() => precarga(legacyPhone, legacyCountry));
  const [touched, setTouched] = useState(false);
  const [etapa, setEtapa] = useState("");
  const [dondeTrabaja, setDondeTrabaja] = useState(
    () => currentUser?.professional_profile?.donde_trabaja || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const show = !loading && needsProfile(currentUser);

  useEffect(() => {
    if (legacyCountry) setCountry(legacyCountry);
    if (legacyPhone) setPhone((prev) => prev || precarga(legacyPhone, legacyCountry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyPhone, legacyCountry]);

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!show) return null;

  const normalizedPhone = normalizeMobile(phone, country);
  const phoneLocalError =
    faltaPhone && touched && phone.trim() && !normalizedPhone ? mensajeCelularInvalido(country) : null;

  const guardarYSeguir = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (faltaPhone && !normalizedPhone) {
      setError(mensajeCelularInvalido(country));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (faltaPhone) {
        const payload = new FormData();
        payload.append("country", country);
        payload.append("phone", normalizedPhone);
        await client.patch("/api/user/profile/", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // #1683 v2 (R59, D11): sin casilla ni PATCH de comunicaciones. El permiso de WhatsApp ya nació
        // al aceptar los Términos; acá solo se avisa (`gate-aviso-whatsapp`).
      }
      const onboardingPatch = {};
      if (faltaEtapa && etapa) onboardingPatch.etapa = etapa;
      if (faltaDonde && dondeTrabaja.trim()) onboardingPatch.donde_trabaja = dondeTrabaja.trim();
      if (Object.keys(onboardingPatch).length > 0) {
        await client.patch("/api/user/onboarding/", onboardingPatch);
      }
      // R58: si dejó etapa o institución en blanco pudiendo llenarlas, es lo
      // mismo que apretar "Después" — no se le vuelve a pedir esta sesión.
      const dejoAlgoPendiente =
        (faltaEtapa && !etapa) || (faltaDonde && !dondeTrabaja.trim());
      if (dejoAlgoPendiente && currentUser?.id) marcarDespues(currentUser.id, currentUser.login_session_id);
      await refreshUser();
    } catch (e) {
      const data = e?.response?.data;
      setError(
        (data && [].concat(data.phone || data.country || data.etapa || data.donde_trabaja || []).join(" ")) ||
          "No pudimos guardar tus datos. Revisa tu conexión y reintenta."
      );
      setSubmitting(false);
    }
  };

  // R45: solo disponible cuando el celular YA es canónico (nunca se salta).
  const despues = () => {
    if (currentUser?.id) marcarDespues(currentUser.id, currentUser.login_session_id);
    refreshUser().catch(() => {});
  };

  const shownError = error || phoneLocalError;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-gate-title"
      data-testid="profile-required-gate"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "rgba(var(--shadow-rgb), 0.72)",
        backdropFilter: "blur(4px)",
      }}
    >
      <Box
        component="form"
        onSubmit={guardarYSeguir}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 440,
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--fg-rgb), 0.1)",
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(var(--shadow-rgb), 0.5)",
          px: { xs: 2.25, sm: 3 },
          py: { xs: 2.5, sm: 3 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              flex: "0 0 auto",
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color: "var(--color-primary-soft)",
              backgroundColor: "rgba(var(--color-primary-rgb), 0.14)",
              border: "1px solid rgba(var(--color-primary-rgb), 0.35)",
            }}
          >
            <PhoneIphoneOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography id="profile-gate-title" component="h2" sx={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--text-primary)" }}>
            Completa tus datos
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "0.9rem", color: "var(--text-secondary)", mb: 2 }}>
          Nos faltan algunas cosas para ayudarte si algo falla. 20 segundos.
        </Typography>

        {faltaPhone && (
          <TextField
            select
            data-testid="profile-field-country"
            fullWidth
            label="País"
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setError(null);
            }}
            disabled={submitting}
            SelectProps={{ native: true }}
            sx={{ mb: 2 }}
            inputProps={{ "aria-label": "País", style: { fontSize: 16 } }}
          >
            {PAISES.map((p) => (
              <option key={p.iso} value={p.iso}>
                {`${banderaPais(p.iso)} ${p.nombre} (+${p.code})`}
              </option>
            ))}
          </TextField>
        )}

        {faltaPhone && (
          <TextField
            data-testid="profile-field-phone"
            autoFocus
            fullWidth
            label="Tu celular"
            placeholder={placeholderCelular(country)}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setError(null);
            }}
            onBlur={() => setTouched(true)}
            disabled={submitting}
            error={Boolean(shownError)}
            helperText={shownError || " "}
            sx={{ mb: 1 }}
            inputProps={{
              inputMode: "tel",
              autoComplete: "tel",
              "aria-label": "Celular",
              style: { fontSize: 16 },
            }}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "var(--text-muted)", fontSize: 16, whiteSpace: "nowrap" }}>
                  {prefijoCelular(country)}
                </Typography>
              ),
            }}
          />
        )}

        {faltaPhone && <AvisoWhatsapp testid="gate-aviso-whatsapp" lugar="celular" sx={{ mb: 1.5 }} />}

        {faltaEtapa && (
          <Box data-testid="profile-field-etapa" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "var(--text-muted)", mb: 0.75 }}>¿Qué eres?</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              {ETAPA_OPCIONES.map((o) => (
                <Box
                  key={o.value}
                  role="button"
                  tabIndex={0}
                  data-testid={`profile-field-etapa-${o.value}`}
                  onClick={() => setEtapa(o.value)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setEtapa(o.value)}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    border: `1px solid ${etapa === o.value ? "var(--color-primary)" : "rgba(var(--fg-rgb),0.18)"}`,
                    bgcolor: etapa === o.value ? "rgba(var(--color-primary-rgb),0.14)" : "transparent",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: etapa === o.value ? "var(--text-primary)" : "var(--text-muted)",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {o.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {faltaDonde && (
          <TextField
            data-testid="profile-field-donde_trabaja"
            fullWidth
            label="Dónde trabajas"
            value={dondeTrabaja}
            onChange={(e) => setDondeTrabaja(e.target.value)}
            disabled={submitting}
            sx={{ mb: 1 }}
            inputProps={{ "aria-label": "Dónde trabajas", style: { fontSize: 16 } }}
          />
        )}

        <AdminButton
          type="submit"
          fullWidth
          variant="primary"
          disabled={faltaPhone && !phone.trim()}
          loading={submitting}
          sx={{ mt: 1, height: 44, justifyContent: "center" }}
        >
          Guardar y seguir
        </AdminButton>

        {/* R45: solo si el celular YA está resuelto (no falta) — nunca se
            ofrece "Después" mientras falte el celular (R43). */}
        {!faltaPhone && (
          <AdminButton
            fullWidth
            variant="ghost"
            data-testid="profile-gate-later"
            disabled={submitting}
            onClick={despues}
            sx={{ mt: 1, justifyContent: "center" }}
          >
            Después
          </AdminButton>
        )}

        <AdminButton
          fullWidth
          variant="ghost"
          data-testid="profile-gate-logout"
          disabled={submitting}
          onClick={() => logout()}
          sx={{ mt: 1, justifyContent: "center", minHeight: 44 }} // R60 (revisor v7 B11)
        >
          Cerrar sesión
        </AdminButton>
      </Box>
    </Box>
  );
};

export default ProfileRequiredGate;
